import type { Plan, PlanStatus } from '../lib/api'

/**
 * The ONE place the (plan, status) pair becomes what a customer is shown, so the shell chrome and
 * the Plans page cannot disagree about whether continuous monitoring is on -- and so a paying
 * customer is never told they are on the free tier.
 *
 * This is PRESENTATION, not entitlement. The server decides the real entitlement in one place
 * (`auth.is_on_monitor`, surfaced as `Me.monitoringActive`); this only turns the pair into a
 * boolean-for-display and the honest banner line. `monitor/past_due` is not "Free", and
 * `monitor/canceled` is not "Free" either -- each off-state gets wording that is true for it.
 */
export interface PlanPresentation {
  /** For display only. The shell prefers the server's `Me.monitoringActive`; this matches it. */
  monitoringActive: boolean
  /** The paywall/notice line, or null when monitoring is active (there is nothing to say). */
  banner: string | null
}

export function planPresentation(plan: Plan | null, planStatus: PlanStatus | null): PlanPresentation {
  if (plan === 'monitor' && planStatus === 'active') {
    return { monitoringActive: true, banner: null }
  }
  if (plan === 'monitor' && planStatus === 'past_due') {
    return {
      monitoringActive: false,
      banner: 'Your Monitor plan is past due, so continuous monitoring is paused. Your report remains readable.',
    }
  }
  if (plan === 'free') {
    return {
      monitoringActive: false,
      banner: 'Free assessment — your report stays readable, continuous monitoring is off.',
    }
  }
  // monitor/canceled, monitor/none, or anything unexpected: monitoring is off, but do NOT call it
  // "Free" (they were a paying customer) and do NOT invent a state. A plain, true, generic line.
  return {
    monitoringActive: false,
    banner: 'Continuous monitoring is off. Your report remains readable.',
  }
}
